import React, { useState } from 'react';
import { motion } from 'framer-motion';

const ResponsiveTest = () => {
  const [currentBreakpoint, setCurrentBreakpoint] = useState('');

  // Check current breakpoint
  React.useEffect(() => {
    const checkBreakpoint = () => {
      const width = window.innerWidth;
      if (width < 640) setCurrentBreakpoint('Mobile (< 640px)');
      else if (width < 768) setCurrentBreakpoint('Small Tablet (640px - 767px)');
      else if (width < 1024) setCurrentBreakpoint('Tablet (768px - 1023px)');
      else if (width < 1280) setCurrentBreakpoint('Desktop (1024px - 1279px)');
      else setCurrentBreakpoint('Large Desktop (≥ 1280px)');
    };

    checkBreakpoint();
    window.addEventListener('resize', checkBreakpoint);
    return () => window.removeEventListener('resize', checkBreakpoint);
  }, []);

  return (
    <div className="min-h-screen w-full bg-gradient-to-br from-purple-50 to-blue-50 p-4">
      <div className="max-w-6xl mx-auto">
        {/* Header */}
        <div className="text-center mb-8">
          <h1 className="text-3xl sm:text-4xl lg:text-5xl font-bold text-gray-900 mb-4">
            Responsive Design Test
          </h1>
          <p className="text-lg text-gray-600 mb-2">
            Current Breakpoint: <span className="font-semibold text-purple-600">{currentBreakpoint}</span>
          </p>
          <p className="text-sm text-gray-500">
            Window Width: <span className="font-mono">{window.innerWidth}px</span>
          </p>
        </div>

        {/* Responsive Grid Test */}
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-4 mb-8">
          {[1, 2, 3, 4, 5, 6, 7, 8].map((item) => (
            <motion.div
              key={item}
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: item * 0.1 }}
              className="card hover:shadow-lg transition-all duration-300"
            >
              <div className="text-center p-6">
                <div className="w-12 h-12 bg-purple-100 rounded-full flex items-center justify-center mx-auto mb-4">
                  <span className="text-2xl">📱</span>
                </div>
                <h3 className="font-semibold text-gray-900 mb-2">Card {item}</h3>
                <p className="text-sm text-gray-600">
                  This card adapts to different screen sizes using responsive grid classes.
                </p>
              </div>
            </motion.div>
          ))}
        </div>

        {/* Responsive Typography Test */}
        <div className="card mb-8">
          <h2 className="text-2xl sm:text-3xl lg:text-4xl font-bold text-gray-900 mb-6">
            Responsive Typography
          </h2>
          <div className="space-y-4">
            <p className="text-sm sm:text-base lg:text-lg text-gray-700">
              This text scales responsively using clamp() functions and responsive classes.
            </p>
            <p className="text-xs sm:text-sm lg:text-base text-gray-600">
              Smaller text also adapts to screen size for optimal readability.
            </p>
          </div>
        </div>

        {/* Responsive Spacing Test */}
        <div className="card mb-8">
          <h2 className="text-2xl font-bold text-gray-900 mb-6">Responsive Spacing</h2>
          <div className="space-y-4 sm:space-y-6 lg:space-y-8">
            <div className="bg-purple-100 p-4 sm:p-6 lg:p-8 rounded-lg">
              <p className="text-center text-purple-800 font-medium">
                This box uses responsive padding that increases with screen size
              </p>
            </div>
            <div className="bg-blue-100 p-4 sm:p-6 lg:p-8 rounded-lg">
              <p className="text-center text-blue-800 font-medium">
                Another box with responsive spacing
              </p>
            </div>
          </div>
        </div>

        {/* Responsive Button Test */}
        <div className="card mb-8">
          <h2 className="text-2xl font-bold text-gray-900 mb-6">Responsive Buttons</h2>
          <div className="flex flex-col sm:flex-row gap-4">
            <button className="btn-primary flex-1">
              Primary Button
            </button>
            <button className="btn-secondary flex-1">
              Secondary Button
            </button>
            <button className="btn-outline flex-1">
              Outline Button
            </button>
          </div>
        </div>

        {/* Responsive Form Test */}
        <div className="card mb-8">
          <h2 className="text-2xl font-bold text-gray-900 mb-6">Responsive Form</h2>
          <div className="space-y-4">
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              <input
                type="text"
                placeholder="First Name"
                className="input-field"
              />
              <input
                type="text"
                placeholder="Last Name"
                className="input-field"
              />
            </div>
            <input
              type="email"
              placeholder="Email Address"
              className="input-field"
            />
            <textarea
              placeholder="Message"
              className="textarea-field"
              rows="4"
            />
          </div>
        </div>

        {/* Responsive Navigation Test */}
        <div className="card mb-8">
          <h2 className="text-2xl font-bold text-gray-900 mb-6">Responsive Navigation</h2>
          <div className="flex flex-col sm:flex-row gap-2 sm:gap-4">
            <button className="nav-tab active">Home</button>
            <button className="nav-tab">About</button>
            <button className="nav-tab">Services</button>
            <button className="nav-tab">Contact</button>
          </div>
        </div>

        {/* Responsive Status Badges */}
        <div className="card mb-8">
          <h2 className="text-2xl font-bold text-gray-900 mb-6">Responsive Status Badges</h2>
          <div className="flex flex-wrap gap-2 sm:gap-4">
            <span className="status-badge status-success">Success</span>
            <span className="status-badge status-warning">Warning</span>
            <span className="status-badge status-error">Error</span>
            <span className="status-badge status-info">Info</span>
            <span className="status-badge status-neutral">Neutral</span>
          </div>
        </div>

        {/* Responsive Modal Test */}
        <div className="card mb-8">
          <h2 className="text-2xl font-bold text-gray-900 mb-6">Responsive Modal</h2>
          <p className="text-gray-600 mb-4">
            Modals use responsive max-width and padding that adapts to screen size.
          </p>
          <button className="btn-primary">
            Open Modal (if implemented)
          </button>
        </div>

        {/* Responsive Chat Test */}
        <div className="card mb-8">
          <h2 className="text-2xl font-bold text-gray-900 mb-6">Responsive Chat Layout</h2>
          <div className="chat-container bg-gray-50 rounded-lg overflow-hidden">
            <div className="chat-header bg-white border-b">
              <div className="p-4">
                <h3 className="font-semibold">Chat Header</h3>
                <p className="text-sm text-gray-600">Responsive chat header with safe area support</p>
              </div>
            </div>
            <div className="chat-messages">
              <div className="p-4">
                <p className="text-sm text-gray-600">Chat messages area with responsive scrolling</p>
              </div>
            </div>
            <div className="chat-input-area bg-white border-t">
              <div className="p-4">
                <input
                  type="text"
                  placeholder="Type a message..."
                  className="chat-input"
                />
              </div>
            </div>
          </div>
        </div>

        {/* Responsive Bottom Navigation Test */}
        <div className="card mb-8">
          <h2 className="text-2xl font-bold text-gray-900 mb-6">Responsive Bottom Navigation</h2>
          <div className="bg-white border rounded-lg p-4">
            <div className="bottom-nav-items">
              <div className="bottom-nav-item active">
                <span className="text-2xl">🏠</span>
                <span className="text-xs">Home</span>
              </div>
              <div className="bottom-nav-item">
                <span className="text-2xl">📋</span>
                <span className="text-xs">Cases</span>
              </div>
              <div className="bottom-nav-item">
                <span className="text-2xl">💬</span>
                <span className="text-xs">Chat</span>
              </div>
              <div className="bottom-nav-item">
                <span className="text-2xl">💰</span>
                <span className="text-xs">Wallet</span>
              </div>
            </div>
          </div>
        </div>

        {/* Responsive Utilities Test */}
        <div className="card">
          <h2 className="text-2xl font-bold text-gray-900 mb-6">Responsive Utilities</h2>
          <div className="space-y-4">
            <div className="bg-gray-100 p-4 rounded-lg">
              <p className="line-clamp-2">
                This text uses line-clamp utility to limit to 2 lines and prevent overflow on smaller screens.
                It will automatically truncate with ellipsis when the content is too long for the container.
              </p>
            </div>
            <div className="bg-gray-100 p-4 rounded-lg">
              <p className="text-balance">
                This text uses text-balance utility for better text wrapping and readability across different screen sizes.
              </p>
            </div>
            <div className="bg-gray-100 p-4 rounded-lg">
              <p className="text-gradient font-bold">
                This text uses gradient utility for visual appeal
              </p>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default ResponsiveTest;
